import { analyticsConsentGranted } from './consent';
import { apiUrl } from './backendUrl';
import {
  getOutreachAnalyticsDimensions,
  getOutreachCalculatorMode,
} from '../content/outreachLandingPages';

const PUBLIC_PATHS = new Set([
  '/',
  '/product',
  '/pricing',
  '/about',
  '/solutions/board-meeting-preparation',
  '/why-scenarios',
  '/security',
  '/try/team-intelligence',
  '/try/strategy-rhythm',
  '/try/before-everyone-agrees',
]);
const PUBLIC_ANALYTICS_QUEUE = '__affinityaiPublicAnalyticsQueue';
const SCRIPT_ID = 'affinityai-ga4';
const CAMPAIGN_VALUE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/;
const CTA_VALUE = /^[a-z0-9][a-z0-9_]{0,63}$/;
const MEASUREMENT_ID = /^G-[A-Z0-9]{6,20}$/;
const env = typeof process !== 'undefined' ? process.env || {} : {};
const state = {
  initialized: false,
  configured: false,
  lastPageKey: '',
  measurementId: '',
  attribution: null,
  attributionRef: '',
  capturePromise: null,
  outreachObservationKeys: new Set(),
  outreachEventIds: new Map(),
  businessCaseInteractionKeys: new Set(),
};

function clean(value, pattern = CAMPAIGN_VALUE) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return pattern.test(normalized) ? normalized : '';
}

export function readPublicAnalyticsConfig({ hostname = globalThis.location?.hostname } = {}) {
  const runtime = typeof window !== 'undefined' ? window.__AFFINITYAI_CONFIG__ || {} : {};
  const enabled =
    runtime.PUBLIC_ANALYTICS_ENABLED === true ||
    String(env.REACT_APP_PUBLIC_ANALYTICS_ENABLED || '').toLowerCase() === 'true';
  const measurementId = clean(
    runtime.GA4_MEASUREMENT_ID || env.REACT_APP_GA4_MEASUREMENT_ID || '',
    MEASUREMENT_ID,
  );
  const configuredEnvironment = clean(
    runtime.GA4_ENVIRONMENT || env.REACT_APP_GA4_ENVIRONMENT || '',
    /^(preview|production)$/,
  );
  const expectedEnvironment =
    hostname === 'www.affinityai.app' || hostname === 'affinityai.app'
      ? 'production'
      : hostname === 'localhost' || hostname === '127.0.0.1'
        ? 'local'
        : 'preview';
  return {
    enabled:
      enabled &&
      Boolean(measurementId) &&
      configuredEnvironment === expectedEnvironment &&
      expectedEnvironment !== 'local',
    measurementId,
    configuredEnvironment,
    expectedEnvironment,
  };
}

export function isPublicAnalyticsPath(pathname = globalThis.location?.pathname || '') {
  return PUBLIC_PATHS.has(pathname);
}

export function shouldInitializePublicAnalytics({ pathname, authResolved, authenticated }) {
  if (!isPublicAnalyticsPath(pathname) || !authResolved) return false;
  return pathname !== '/' || !authenticated;
}

export function parseCampaignAttribution(
  search = globalThis.location?.search || '',
  pathname = globalThis.location?.pathname || '',
) {
  const params = new URLSearchParams(search);
  const campaignId = clean(params.get('utm_campaign'));
  const utmId = clean(params.get('utm_id'));
  const source = clean(params.get('utm_source'));
  const medium = clean(params.get('utm_medium'));
  const messageVariant = clean(params.get('utm_content'));
  const landingVariant = clean(params.get('landing_variant'));
  const copyRevision = clean(params.get('copy_revision'));
  const senderVariant = clean(params.get('sender_variant'));
  const aid = clean(params.get('aid'), /^[A-Za-z0-9_-]{20,180}$/);
  if (!campaignId || !source || !medium) return null;
  if (utmId && utmId !== campaignId) return null;
  const locked = getOutreachAnalyticsDimensions(pathname) || {};
  return {
    campaignId,
    source,
    medium,
    ...(locked.messageVariant || messageVariant
      ? { messageVariant: locked.messageVariant || messageVariant }
      : {}),
    ...(locked.landingVariant || landingVariant
      ? { landingVariant: locked.landingVariant || landingVariant }
      : {}),
    ...(locked.copyRevision || copyRevision
      ? { copyRevision: locked.copyRevision || copyRevision }
      : {}),
    ...(locked.senderVariant || senderVariant
      ? { senderVariant: locked.senderVariant || senderVariant }
      : {}),
    ...(aid ? { aid } : {}),
  };
}

export function removeAidFromVisibleUrl() {
  if (typeof window === 'undefined' || !window.location?.href) return false;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('aid')) return false;
  url.searchParams.delete('aid');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return true;
}

function publicDimensions(
  attribution = state.attribution,
  pathname = globalThis.location?.pathname || '',
) {
  const locked = getOutreachAnalyticsDimensions(pathname) || {};
  const dimensions = { ...(attribution || {}), ...locked };
  return {
    ...(dimensions.campaignId ? { campaign_id: dimensions.campaignId } : {}),
    ...(dimensions.source ? { campaign_source: dimensions.source } : {}),
    ...(dimensions.medium ? { campaign_medium: dimensions.medium } : {}),
    ...(dimensions.messageVariant ? { message_variant: dimensions.messageVariant } : {}),
    ...(dimensions.landingVariant ? { landing_variant: dimensions.landingVariant } : {}),
    ...(dimensions.copyRevision ? { copy_revision: dimensions.copyRevision } : {}),
    ...(dimensions.senderVariant ? { sender_variant: dimensions.senderVariant } : {}),
  };
}

function outreachEventId(key) {
  if (!state.outreachEventIds.has(key)) {
    const random =
      globalThis.crypto?.randomUUID?.() || `outreach_${Math.random().toString(36).slice(2, 18)}`;
    state.outreachEventIds.set(key, random);
  }
  return state.outreachEventIds.get(key);
}

function publicGtag() {
  window[PUBLIC_ANALYTICS_QUEUE].push(arguments);
}

export function deactivatePublicAnalytics() {
  if (typeof window === 'undefined') return false;
  if (state.initialized && window[PUBLIC_ANALYTICS_QUEUE]) {
    publicGtag('consent', 'update', { analytics_storage: 'denied' });
  }
  document.getElementById(SCRIPT_ID)?.remove();
  delete window[PUBLIC_ANALYTICS_QUEUE];
  delete window.affinityaiPublicGtag;
  state.initialized = false;
  state.configured = false;
  state.lastPageKey = '';
  state.measurementId = '';
  return true;
}

async function captureAttribution(attribution) {
  const response = await fetch(apiUrl('/api/outreach-attribution/capture'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(attribution),
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !/^[a-f0-9]{64}$/.test(data?.attributionRef || '')) {
    throw new Error('attribution_capture_failed');
  }
  return data.attributionRef;
}

function initializeTag({ hostname } = {}) {
  if (typeof window === 'undefined' || !analyticsConsentGranted()) return false;
  const config = readPublicAnalyticsConfig({ hostname });
  if (!config.enabled || !isPublicAnalyticsPath()) return false;
  if (state.initialized && state.measurementId !== config.measurementId) return false;
  if (state.initialized) {
    publicGtag('consent', 'update', { analytics_storage: 'granted' });
    return true;
  }
  if (!window[PUBLIC_ANALYTICS_QUEUE]) window[PUBLIC_ANALYTICS_QUEUE] = [];
  window.affinityaiPublicGtag = publicGtag;
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.measurementId)}&l=${PUBLIC_ANALYTICS_QUEUE}`;
    document.head.appendChild(script);
  }
  state.initialized = true;
  state.measurementId = config.measurementId;
  publicGtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  if (!state.configured) {
    publicGtag('js', new Date());
    publicGtag('config', config.measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    state.configured = true;
  }
  return true;
}

export function trackPublicPageView({ pathname, search = '', hostname } = {}) {
  const pagePath = pathname || globalThis.location?.pathname || '';
  if (!isPublicAnalyticsPath(pagePath)) {
    deactivatePublicAnalytics();
    return false;
  }
  state.attribution = state.attribution || parseCampaignAttribution(search, pagePath);
  if (!initializeTag({ hostname })) return false;
  const dimensions = publicDimensions(state.attribution, pagePath);
  const key = `${pagePath}|${dimensions.landing_variant || ''}|${dimensions.copy_revision || ''}`;
  if (state.lastPageKey === key) return false;
  state.lastPageKey = key;
  publicGtag('event', 'page_view', { page_path: pagePath, ...dimensions });
  if (getOutreachAnalyticsDimensions(pagePath) && !state.outreachObservationKeys.has(key)) {
    state.outreachObservationKeys.add(key);
    const analyticsConfig = readPublicAnalyticsConfig({ hostname });
    publicGtag('event', 'outreach_landing_view', {
      analytics_contract_version: 2,
      analytics_environment: analyticsConfig.configuredEnvironment,
      event_id: outreachEventId(key),
      landing_page_route: pagePath,
      ...dimensions,
    });
  }
  return true;
}

export function trackPrimaryCtaClick({ ctaId, pathname, hostname } = {}) {
  const pagePath = pathname || globalThis.location?.pathname || '';
  const safeCtaId = clean(ctaId, CTA_VALUE);
  if (!safeCtaId || !isPublicAnalyticsPath(pagePath) || !initializeTag({ hostname })) return false;
  publicGtag('event', 'primary_cta_click', {
    page_path: pagePath,
    cta_id: safeCtaId,
    ...publicDimensions(state.attribution, pagePath),
  });
  return true;
}

export function trackBusinessCaseInteraction({
  variantId,
  copyRevision,
  calculatorMode,
  pathname,
  hostname,
} = {}) {
  const pagePath = pathname || globalThis.location?.pathname || '';
  const locked = getOutreachAnalyticsDimensions(pagePath);
  const lockedCalculatorMode = getOutreachCalculatorMode(pagePath);
  if (
    !locked ||
    locked.landingVariant !== clean(variantId) ||
    locked.copyRevision !== clean(copyRevision) ||
    lockedCalculatorMode !== clean(calculatorMode, /^(time_value|strategic_coverage)$/) ||
    !initializeTag({ hostname })
  )
    return false;
  const key = `${pagePath}|${locked.landingVariant}|${locked.copyRevision}|${lockedCalculatorMode}`;
  if (state.businessCaseInteractionKeys.has(key)) return false;
  state.businessCaseInteractionKeys.add(key);
  publicGtag('event', 'business_case_interacted', {
    page_path: pagePath,
    calculator_mode: lockedCalculatorMode,
    ...publicDimensions(state.attribution, pagePath),
  });
  return true;
}

export function getApprovedAttributionForBinding(pathname = globalThis.location?.pathname || '') {
  if (!analyticsConsentGranted()) return null;
  const current = state.attribution || parseCampaignAttribution(undefined, pathname);
  if (!current?.aid) return null;
  return {
    ...current,
    ...(getOutreachAnalyticsDimensions(pathname) || {}),
  };
}

export function getAttributionRefFromSearch(search = globalThis.location?.search || '') {
  const value = String(new URLSearchParams(search).get('attribution_ref') || '')
    .trim()
    .toLowerCase();
  return /^[a-f0-9]{64}$/.test(value) ? value : '';
}

export function appendAttributionRef(destination, attributionRef) {
  const ref = String(attributionRef || '')
    .trim()
    .toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(ref)) return '';
  try {
    const url = new URL(destination, globalThis.location?.origin || 'https://www.affinityai.app');
    url.searchParams.set('attribution_ref', ref);
    return url.toString();
  } catch (_) {
    return '';
  }
}

export function handleAnalyticsConsentChange(decision) {
  if (decision?.analytics === true) {
    trackPublicPageView();
    return;
  }
  if (state.initialized && typeof window !== 'undefined') {
    publicGtag('consent', 'update', { analytics_storage: 'denied' });
  }
}

export function installPublicAnalytics() {
  if (typeof window === 'undefined') return () => {};
  state.attribution = parseCampaignAttribution();
  state.attributionRef = getAttributionRefFromSearch();
  removeAidFromVisibleUrl();
  const clickHandler = (event) => {
    const link = event.target?.closest?.('[data-affinityai-primary-cta="true"]');
    if (link) trackPrimaryCtaClick({ ctaId: link.dataset.ctaId });
    const anchor = event.target?.closest?.('a[href]');
    if (
      !anchor ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    let destination;
    try {
      destination = new URL(anchor.href, window.location.origin);
    } catch (_) {
      return;
    }
    if (
      destination.origin !== window.location.origin ||
      (!isPublicAnalyticsPath(destination.pathname) && destination.pathname !== '/start')
    )
      return;
    if (state.attributionRef) {
      anchor.href = appendAttributionRef(destination, state.attributionRef);
      return;
    }
    const attribution = getApprovedAttributionForBinding(window.location.pathname);
    if (!attribution) return;
    event.preventDefault();
    state.capturePromise =
      state.capturePromise ||
      Promise.race([
        captureAttribution(attribution),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('attribution_capture_timeout')), 1200),
        ),
      ]);
    state.capturePromise
      .then((attributionRef) => {
        state.attributionRef = attributionRef;
        const current = new URL(window.location.href);
        current.searchParams.delete('aid');
        current.searchParams.set('attribution_ref', attributionRef);
        window.history.replaceState(
          window.history.state,
          '',
          `${current.pathname}${current.search}${current.hash}`,
        );
        window.location.assign(appendAttributionRef(destination, attributionRef));
      })
      .catch(() => window.location.assign(destination.toString()));
  };
  const consentHandler = (event) => handleAnalyticsConsentChange(event.detail);
  document.addEventListener('click', clickHandler, true);
  window.addEventListener('affinityai:consent-changed', consentHandler);
  return () => {
    document.removeEventListener('click', clickHandler, true);
    window.removeEventListener('affinityai:consent-changed', consentHandler);
  };
}

export function __resetPublicAnalyticsForTests() {
  state.initialized = false;
  state.configured = false;
  state.lastPageKey = '';
  state.measurementId = '';
  state.attribution = null;
  state.attributionRef = '';
  state.capturePromise = null;
  state.outreachObservationKeys = new Set();
  state.outreachEventIds = new Map();
  state.businessCaseInteractionKeys = new Set();
}
